package com.sddamico.draftmachine.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.roleSmall
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Button as DomButton

/**
 * A selectable / removable filter chip — brushed inset by default, copper-lit when [active].
 * Ports `components/core/Tag.jsx`.
 *
 * @param icon bare Phosphor glyph name.
 * @param onRemove renders a trailing "x" button; the click stops propagation.
 * @param onClick makes the whole chip pressable (and switches the cursor to a pointer).
 * @param content optional slot; otherwise [label] is rendered.
 */
@Composable
fun Tag(
  label: String? = null,
  modifier: Modifier = Modifier,
  icon: String? = null,
  active: Boolean = false,
  onRemove: (() -> Unit)? = null,
  onClick: (() -> Unit)? = null,
  content: (@Composable () -> Unit)? = null,
) {
  val bg = if (active) DmColor.brassTint(0.16f) else DmColor.surfaceInset
  val fg = if (active) DmColor.brass300 else DmColor.textSecondary
  val bd = if (active) DmColor.brass600 else DmColor.borderStrong

  val clickHandler = onClick
  val mod = Modifier
    .displayInlineFlex()
    .alignItems(AlignItems.Center)
    .gap(DmSpacing.space2)
    .padding(topBottom = 4.px, leftRight = DmSpacing.space3)
    .height(28.px)
    .backgroundColor(bg)
    .color(fg)
    .border(1.px, LineStyle.Solid, bd)
    .borderRadius(DmEffects.radiusSm)
    .roleSmall()
    .fontWeight(DmType.weightSemibold)
    .whiteSpace(WhiteSpace.NoWrap)
    .cursor(if (clickHandler != null) Cursor.Pointer else Cursor.Default)
    .then(modifier)

  Span(
    attrs = mod.toAttrs {
      if (clickHandler != null) onClick { clickHandler() }
    }
  ) {
    if (icon != null) {
      Icon(icon, IconWeight.Bold, Modifier.fontSize(13.px))
    }
    if (content != null) content() else if (label != null) SpanText(label)
    if (onRemove != null) {
      DomButton(
        attrs = Modifier
          .displayInlineFlex()
          .margin(left = 2.px)
          .cursor(Cursor.Pointer)
          .opacity(0.7)
          .styleModifier {
            property("padding", "0")
            property("background", "none")
            property("border", "none")
            property("color", "inherit")
          }
          .toAttrs {
            attr("aria-label", "Remove")
            onClick { evt ->
              evt.stopPropagation()
              onRemove()
            }
          }
      ) {
        Icon("x", IconWeight.Bold, Modifier.fontSize(12.px))
      }
    }
  }
}
